import type { BuildPlanConcept, BuildPlanResponse } from '@/types/api';

/** Key ideas are the most central fifth of the plan, and at least this
 *  central: most concepts score high, so a fixed bar starred nearly all. */
export const KEY_IDEA_AT = 0.8;
const KEY_IDEA_SHARE = 0.2;

export type PlanLevel = 'foundational' | 'core' | 'advanced';

export type PlanRow =
  | {
      kind: 'section';
      key: string;
      number: number;
      title: string;
      goal: string;
    }
  | {
      kind: 'concept';
      key: string;
      name: string;
      points: number;
      buildsOn: string[];
      level: PlanLevel;
      keyIdea: boolean;
    };

function level(value: string | undefined): PlanLevel {
  return value === 'foundational' || value === 'advanced' ? value : 'core';
}

/** The importance a concept needs to be starred in this plan. */
export function keyIdeaBar(plan: BuildPlanResponse): number {
  const scores = (plan.sections ?? [])
    .flatMap(s => s.concepts ?? [])
    .map(c => c.importance ?? 0)
    .sort((a, b) => b - a);
  const top = Math.max(1, Math.round(scores.length * KEY_IDEA_SHARE));
  return Math.max(KEY_IDEA_AT, scores[top - 1] ?? Infinity);
}

function conceptRow(c: BuildPlanConcept, key: string, bar: number): PlanRow {
  return {
    kind: 'concept',
    key,
    name: c.name,
    points: c.points ?? 0,
    buildsOn: c.builds_on ?? [],
    level: level(c.level),
    keyIdea: (c.importance ?? 0) >= bar,
  };
}

/** The plan as the card reveals it: each section, then its concepts. */
export function planRows(plan: BuildPlanResponse): PlanRow[] {
  const bar = keyIdeaBar(plan);
  return (plan.sections ?? []).flatMap((s, i) => [
    {
      kind: 'section' as const,
      key: `s${i}`,
      number: i + 1,
      title: s.title,
      goal: s.goal ?? '',
    },
    ...(s.concepts ?? []).map((c, j) => conceptRow(c, `s${i}c${j}`, bar)),
  ]);
}

/** "6 sections · 25 concepts · 92 key points". */
export function planCounts(plan: BuildPlanResponse): string {
  const sections = plan.sections ?? [];
  const concepts = sections.reduce((n, s) => n + (s.concepts?.length ?? 0), 0);
  const parts = [
    `${sections.length} ${sections.length === 1 ? 'section' : 'sections'}`,
    `${concepts} ${concepts === 1 ? 'concept' : 'concepts'}`,
  ];
  if (plan.key_points) parts.push(`${plan.key_points} key points`);
  return parts.join(' · ');
}
